// @flow
import * as React from 'react';
import { get, isEmpty, isEqual, noop } from 'lodash';
import cookie from 'js-cookie';
import moment from 'moment';

import * as routeBuilder from 'Routes/routeBuilder';
import { withRouter } from 'Client/router/withRouter';
import { PriceCalendar } from 'Components/widgets/booking-form/price-calendar/PriceCalendar.jsx';
import { hasErroredProposals } from 'Utils/proposal/proposalUtils';
import ReactAppContext from 'Context/ReactAppContext';
import { isWinterResort } from 'Utils/resort/resortUtils';
import { getFormattedKidsBirthdate } from 'Utils/trip-information/tripInformationUtils';
import { COLORS } from 'Components/utils/color/colors';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer.jsx';
import { GridCell } from 'Components/frameworks/grid-cell/Grid.cell.jsx';
import { BookingCriteriaContainer } from 'Components/widgets/booking-criteria/BookingCriteria.container.jsx';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
import { BookingErrors } from 'Components/widgets/booking-errors/BookingErrors.jsx';
import { AlternativeProposalsModal } from 'Components/widgets/alternative-proposals-modal/AlternativeProposalsModal.jsx';
import { isSeller } from 'Utils/seller/sellerUtils';
import { getAllAttendeesIds } from 'Components/widgets/contact-info/ContactInfoUtils';
import { BOOKING_ENGINE_ENTRIES, type BookingEngineEntry } from 'Store/booking/constants';
import { setDepartureDate } from 'Store/trip-criteria/departure-date/departureDate.action';
import {
  type FetchProposalsParams,
  type AdditionalTrackingParams,
} from 'Store/booking/proposals/proposals.action';
import { addTranslationKeys } from 'Src/i18n';

import {
  COOKIE_WARNING_LOCALSTORAGE_KEY,
  COOKIES_ARRAY,
} from 'Components/widgets/cookie-warning/constants';
import {
  PRICE_COUNTER_PROPOSAL_SELLER_THRESHOLD,
  PRICE_COUNTER_PROPOSAL_THRESHOLD,
} from 'Store/booking/proposals/constants';
import { PRICE_REMOTE_STICKY_WRAPPER_SELECTOR } from 'Components/widgets/price-remote/constants';
import { fetchBookingEngineI18nQuery } from 'Views/booking/BookingEngineI18n.query';
import {
  getBookingEngineUri,
  PAGE_BE_ACCOMMODATION,
  getBookingCriteriaOffsetSell,
  checkIsDepartureDateAvailaible,
} from 'Utils/booking-engine-pages/BookingPagesUtils';
import { BOOKING_ENGINE_FORM_SELECTOR } from 'Components/widgets/booking-engine-form/constants';

const OFFSET_SELECTOR = '.Navigation-bar';
const COLS = { M: 9 };

export type BookingRedirectParams = {
  adults: ?number,
  children: ?number,
  birthdays: ?Array<string>,
  duration: ?number,
  departureDate: ?number,
  arrivalDate: ?number,
  resortId: ?string,
  transport: ?string,
};

type Props = {
  adultsNumber: number,
  arrivalDate: ?number, // parseInt de "YYYYMMDD",
  selectedResort: ?number,
  bookingConfig: {
    enableTransfer: ?boolean,
    form: {
      title: ?string,
      dateFormat: ?string,
      dateValidate: ?string,
      selectDuration: ?string,
      durationTitle: ?string,
      selectChildrenLabels: ?*,
      selectAdultsLabels: ?*,
      errorMaxGuestsLabel: ?string,
    },
    priceCalendar: {
      offers: {
        urgentisation: {
          label: string,
          rateToDisplay: number,
        },
      },
    },
  },
  bookingCriteria: {
    initialNumberOfChildren: ?number,
    initialNumberOfAdults: ?number,
    initialDuration: ?number,
    initialTransport: ?string,
    departureCities: Array<*>,
  },
  bookingEngineEntry: BookingEngineEntry,
  checkPriceLabel: ?string,
  clearProposals: () => void,
  config: CMSContent,
  endLoading: () => void,
  startLoading: () => void,
  cmsPrice: ?*,
  cmsStartingPrice: ?*,
  dataSelector: string,
  departureDate: ?number, // parseInt de "YYYYMMDD"
  departureCity: string,
  duration: number,
  goToLegacyBookingEngine: (BookingRedirectParams, string) => void,
  i18n: *,
  kids: Array<*>,
  legacyResortRedirect: string,
  package: ?*,
  proposals: Array<Proposal>,
  selectedProposalId: ?number,
  selectProposal: (*, *) => void,
  pushImage: string,
  renderErrors: (*, Array<*>, boolean) => ?React.Node,
  resort: Resort,
  transfer: ?*,
  transport: ?*,
  travelAdvisorConfig: ?*,
  updateTripDateCriteria: (*) => void,
  setDepartureDate: typeof setDepartureDate,
  clearSelectedProposal: (*) => void,
  clearProposals: (*) => void,
  version: 'a' | 'b' | 'ab',
  viewMorePackages: ?{
    label: ?string,
    visible: ?boolean,
  },
  history: RouterHistory,
  location: ProductRouterLocation,
  tripInformation: TripInformation,
  fetchProposalsAndRedirect: (
    FetchProposalsParams,
    AdditionalTrackingParams,
    AppContext,
    mixed,
  ) => Promise<*>,
  prepareBe: (*) => void,
  setCms: (*, *) => void,
  setAttendees: BookingAttendeesType => void,
  attendees: BookingAttendeesType,
  suggestions: BookingSuggestions,
};

const defaultProps = {
  clearProposals: noop,
  kids: [],
  i18n: null,
  dataSelector: 'BookingEngineForm',
  fetchProposalsAndRedirect: noop,
  suggestions: {
    dates: [],
    resorts: [],
  },
};

type State = {
  showModal: boolean,
  shouldFetchProposals: boolean,
  isDepartureDateAvailaible: boolean,
};

class BookingEngineForm extends React.Component<Props, State> {
  static defaultProps = defaultProps;
  static contextType = ReactAppContext;

  bookingCriteriaNode: *;

  constructor(props: Props) {
    super(props);

    this.state = {
      showModal: true,
      shouldFetchProposals: false,
      isDepartureDateAvailaible: true,
    };

    this.bookingCriteriaNode = React.createRef();
  }

  scrollToBookingEngineEntryPoint() {
    /**
     * The setTimeout is needed to make sure the modal is closed before trying to scroll
     */
    setTimeout(() => {
      const { bookingEngineEntry } = this.props;
      const { PRICE_CALENDAR, PRICE_REMOTE } = BOOKING_ENGINE_ENTRIES;
      const bookingEngineFormSelector = document.querySelector(BOOKING_ENGINE_FORM_SELECTOR);
      const priceRemoteSelector = document.querySelector(PRICE_REMOTE_STICKY_WRAPPER_SELECTOR);

      if (bookingEngineEntry === PRICE_CALENDAR && bookingEngineFormSelector) {
        // $FlowFixMe the validity of this check in the condition above
        bookingEngineFormSelector.scrollIntoView();
      } else if (bookingEngineEntry === PRICE_REMOTE && priceRemoteSelector) {
        // $FlowFixMe the validity of this check in the condition above
        priceRemoteSelector.scrollIntoView();
      }
    }, 0);
  }

  async setIsDepartureDateAvailaible(departureDate) {
    const { bookingCriteria } = await getBookingCriteriaOffsetSell(
      this.props.resort.id,
      this.context,
    );

    this.setState({
      isDepartureDateAvailaible: !checkIsDepartureDateAvailaible(
        String(departureDate),
        bookingCriteria?.offsetSell,
      ),
    });
  }

  async componentDidMount() {
    // We assume that if we have proposals, it implies we come from the booking engine.
    this.props.clearSelectedProposal();
    this.props.clearProposals();

    await this.setIsDepartureDateAvailaible(this.props.departureDate);

    /**
     * This setTimeout is needed also (plus the one used in scrollToBookingEngineEntryPoint)
     * because of a race condition with RouteChangeListener scroll (a bugfix for hash)
     * See RouteChangeListener.jsx line 29
     */
    setTimeout(() => this.scrollToBookingEngineEntryPoint(), 0);
  }

  /**
   * #10731
   * This means the user comes from a metasearch and has already selected trip criterias.
   * We force fetching proposals to avoid one more click on continue button
   * but we cannot make the query on componentDidMount hook because state is not yet hydrated from query params
   * arrivalDate is mandatory and will be set after calendar did mount.
   * See PriceCalendar & didMountCallback
   * url format: ?duration=6&date=2019-04-30&number_of_adults=2&kids=2015-03-02_2014-12-20&fetch=1&_ab2=b
   */
  setShouldFetchProposals = () => {
    const {
      location: { queryParams },
    } = this.props;
    const { fetch } = queryParams;
    const shouldFetch = Boolean(fetch && `${fetch}` === '1');

    return () => {
      this.setState({ shouldFetchProposals: shouldFetch });
    };
  };

  async componentDidUpdate(prevProps: Props, prevState: State) {
    if (this.props.departureDate !== prevProps.departureDate) {
      await this.setIsDepartureDateAvailaible(this.props.departureDate);
    }

    if (
      this.state.shouldFetchProposals &&
      !prevState.shouldFetchProposals &&
      this.bookingCriteriaNode.current
    ) {
      // $FlowFixMe
      this.bookingCriteriaNode.current.onSubmit();
    }

    const oldProposal = prevProps.proposals[0];
    const newProposal = this.props.proposals[0];
    if (
      !isEqual(oldProposal, newProposal) ||
      !isEqual(prevProps.suggestions, this.props.suggestions)
    ) {
      this.checkLoading();

      // scroll to errors if any
      if (
        hasErroredProposals(this.props.proposals) ||
        (this.hasCounterProposals() && !this.canDisplayCounterProposals())
      ) {
        // the setTimeout is needed to make sure the global loader is closed before trying to scroll
        setTimeout(() => {
          scrollToElement('.Package-error', 600, OFFSET_SELECTOR);
        });
      }
    }

    // makes sure the counter proposal modal will be closed on resort change
    if (prevProps.resort.id !== this.props.resort.id) {
      this.setState({ showModal: false });
    }
  }

  /**
   * In case the fetch proposals return counter proposals the global loader stay so we need to end it ourself
   * IT stay because we want to keep it when we redirect to the BE for prevent any flickering
   */
  checkLoading = () => {
    if (
      this.props.suggestions.dates.length > 0 ||
      this.props.suggestions.durations.length > 0 ||
      // errors are so badly handle that it's easier to check if there is no proposal rather that there is an error
      this.props.proposals.some(({ id }) => !id) ||
      this.props.proposals.some(proposal => proposal.price.gap > PRICE_COUNTER_PROPOSAL_THRESHOLD)
    ) {
      this.props.endLoading();
    }
  };

  canDisplayCounterProposals() {
    return (
      !this.props.proposals.some(
        proposal =>
          proposal.price.gap >= PRICE_COUNTER_PROPOSAL_SELLER_THRESHOLD && !isSeller(this.context),
      ) ||
      this.props.suggestions.dates.length > 0 ||
      this.props.suggestions.durations.length > 0 ||
      this.props.suggestions.resorts.length > 0
    );
  }

  hasCounterProposals() {
    const proposal = this.props.proposals[0];
    const { dates, durations } = this.props.suggestions;
    return (
      dates.length > 0 ||
      durations.length > 0 ||
      (!isEmpty(proposal) &&
        proposal.price &&
        proposal.price.gap > PRICE_COUNTER_PROPOSAL_THRESHOLD)
    );
  }

  handleDepartureDate = (day: ?* = null) => {
    const newDepartureDate = moment(String(day?.id)).valueOf();
    this.props.setDepartureDate(newDepartureDate);
    this.props.updateTripDateCriteria({ day });
  };

  onFormSubmit = (event: Event) => {
    event.preventDefault();
  };

  goToLegacyBookingEngine = () => {
    const dateFormat = get(this.props, 'bookingConfig.form.dateFormat', '');

    this.props.goToLegacyBookingEngine(
      {
        adults: this.props.adultsNumber,
        children: this.props.kids.length,
        birthdays: getFormattedKidsBirthdate(this.props.kids, dateFormat),
        duration: this.props.duration,
        departureDate: this.props.departureDate,
        arrivalDate: this.props.arrivalDate,
        // resort.resortId is needed for legacy because it doesn't handle double season resort like VMOC_WINTER
        // in this case resort.id = VMOC_WINTER and resort.resortId = VMOC
        resortId: get(this.props, 'resort.resortId'),
        transport: this.props.departureCity,
        [COOKIE_WARNING_LOCALSTORAGE_KEY]: cookie.get(COOKIE_WARNING_LOCALSTORAGE_KEY),
        // $FlowFixMe
        ...COOKIES_ARRAY.reduce((acc, key) => ({ ...acc, [key]: cookie.get(key) }), {}),
      },
      dateFormat,
    );
  };

  getPackages = (newArrivalDate: *): Promise<*> => {
    const {
      bookingCriteria,
      fetchProposalsAndRedirect,
      resort,
      departureCity,
      duration,
      i18n,
      tripInformation,
      location,
    } = this.props;
    const productId = resort.id;
    const isJoyView = resort.isJoyview;

    const departureCityOption = bookingCriteria.departureCities.find(
      city => city.value === departureCity,
    );

    const fetchParams = {
      productId,
      departureCity,
      duration,
      isJoyView,
      // $FlowFixMe it's a recomposition
      globalLoaderLabel: i18n.booking.form.proposalsLoaderLabel,
      // $FlowFixMe
      ...(isSeller(this.context) ? getAllAttendeesIds(tripInformation) : null),
      newArrivalDate,
      bookingEngineEntry: BOOKING_ENGINE_ENTRIES.PRICE_CALENDAR,
    };
    const trackingParams = {
      departureCityName: get(departureCityOption, 'name'),
      shouldTrackTransport: get(this.props.package, 'transport.enableTransport', false),
    };

    return fetchProposalsAndRedirect(fetchParams, trackingParams, this.context, location);
  };

  redirectToBE = newArrivalDate => {
    this.setState({ showModal: false });
    this.getPackages(newArrivalDate).then(() => {
      this.setState({ showModal: true });
    });
  };

  shouldDisplayModal = () => {
    return this.hasCounterProposals() && this.canDisplayCounterProposals() && this.state.showModal;
  };
  goToBookingEngine = async () => {
    const {
      resort,
      proposals,
      attendees,
      setCms,
      setAttendees,
      prepareBe,
      history,
      location,
    } = this.props;
    this.setState({ showModal: false });

    const { config, legalInformations } = await fetchBookingEngineI18nQuery(this.context);

    if (config && legalInformations) {
      setCms(config, legalInformations);
      setAttendees(attendees);
    }

    const proposalId = proposals[0].id;

    prepareBe({ resort, proposals: { proposals, resort }, proposalId });
    history.pushRoute(
      getBookingEngineUri(
        PAGE_BE_ACCOMMODATION,
        proposals,
        resort,
        proposalId,
        location.queryParams,
      ),
    );
  };

  render() {
    const failedProposals = hasErroredProposals(this.props.proposals);
    const i18n = {
      ...this.props.i18n,
      adultAge: get(this.props, 'bookingConfig.form.adultAge'),
      bookingCriteria: this.props.bookingCriteria,
      childrenBirthdayDateFormatPlaceholder: get(
        this.props,
        'bookingConfig.form.childrenBirthdayDateFormatPlaceholder',
      ),
      dateFormat: get(this.props, 'bookingConfig.form.dateFormat'),
      dayPlaceholder: get(this.props, 'bookingConfig.form.dayPlaceholder'),
      monthPlaceholder: get(this.props, 'bookingConfig.form.monthPlaceholder'),
      yearPlaceholder: get(this.props, 'bookingConfig.form.yearPlaceholder'),
      dateValidate: get(this.props, 'bookingConfig.form.dateValidate'),
      durationTitle: get(this.props, 'bookingConfig.form.durationTitle'),
      durationOptionsFormat: get(this.props, 'bookingConfig.form.selectDuration'),
      selectAdultsLabels: get(this.props, 'bookingConfig.form.selectAdultsLabels'),
      selectChildrenLabels: get(this.props, 'bookingConfig.form.selectChildrenLabels'),
      legalNotice: get(this.props, 'bookingConfig.form.legalNotice'),
      minAgeInformation: get(this.props, 'resort.minAuthorizedAge.formattedValue'),
      birthdayFormatErrorMessage: get(this.props, 'bookingConfig.form.errors.birthdayFormat'),
      adultAgeErrorMessage: get(this.props, 'bookingConfig.form.errors.adultChild'),
      skiPassIncludedLabel: get(this.props, 'bookingConfig.form.reinsurance.skiPassIncludedLabel'),
      skiPassIncludedDescription: get(
        this.props,
        'bookingConfig.form.reinsurance.skiPassIncludedDescription',
      ),
      kidStayIncludedLabel: get(this.props, 'bookingConfig.form.reinsurance.kidStayIncludedLabel'),
      didYouKnowLabel: get(this.props, 'bookingConfig.form.reinsurance.didYouKnowLabel'),
      kidStayIncludedDescription: get(
        this.props,
        'bookingConfig.form.reinsurance.kidStayIncludedDescription',
      ),
      minAgeErrorMessage: get(this.props, 'resort.minAgeErrorMessage.formattedValue'),
      proposalsLoaderLabel: get(this.props, 'bookingConfig.form.proposalsLoaderLabel'),
    };
    addTranslationKeys({
      booking: this.props.i18n.booking,
      common: this.props.i18n.common,
      priceRemote: this.props.i18n.priceRemote,
      package: this.props.i18n.package,
    });

    return (
      <SiteContainer bgColor={COLORS.GRAY_LIGHTER} hasBorders hasBottomShadow hasTopShadow>
        <form
          id="booking"
          className="BookingForm BookingForm--bookingEngine"
          onSubmit={this.onFormSubmit}
        >
          <div className="BookingForm-inner">
            <h2 className="BookingForm-title">{get(this.props, 'bookingConfig.form.title')}</h2>
            <BookingCriteriaContainer
              myRef={this.bookingCriteriaNode}
              adultOnly={get(this.props, 'resort.adultOnly')}
              isWinterResort={isWinterResort(this.props.resort)}
              adultsNumber={this.props.adultsNumber}
              bookingConfig={this.props.bookingConfig}
              duration={this.props.duration}
              departureDate={this.props.departureDate}
              isDepartureDateAvailaible={this.state.isDepartureDateAvailaible}
              departureCity={this.props.departureCity}
              enableTransfer={get(this.props, 'bookingConfig.enableTransfer')}
              kids={this.props.kids}
              goToLegacyBookingEngine={this.goToLegacyBookingEngine}
              getPackages={this.getPackages}
              isJoyView={get(this.props, 'resort.isJoyview', false)}
              isTransportEnabled={get(this.props, 'package.transport.enableTransport', false)}
              legacyResortRedirect={this.props.legacyResortRedirect}
              pushImage={this.props.pushImage}
              config={this.props.config}
              childAge={get(this.props, 'resort.minAuthorizedAge.value')}
              resort={get(this.props, 'resort')}
              i18n={i18n}
            >
              <GridCell cols={COLS}>
                <PriceCalendar
                  selectedDay={this.props.departureDate}
                  onSelectDate={this.handleDepartureDate}
                  bookingConfig={this.props.bookingConfig}
                  travelAdvisorConfig={this.props.travelAdvisorConfig}
                  cmsStartingPrice={this.props.cmsStartingPrice}
                  cmsPrice={this.props.cmsPrice}
                  duration={this.props.duration}
                  transport={this.props.departureCity}
                  resortId={get(this.props, 'resort.id')}
                  isJoyview={get(this.props, 'resort.isJoyview', false)}
                  didMountCallback={this.setShouldFetchProposals()}
                />
              </GridCell>
            </BookingCriteriaContainer>

            {!this.state.isDepartureDateAvailaible ||
            failedProposals ||
            this.props.proposals.some(
              proposal =>
                proposal.price &&
                proposal.price.gap >= PRICE_COUNTER_PROPOSAL_SELLER_THRESHOLD &&
                !isSeller(this.context),
            ) ? (
              <BookingErrors
                dataSelector={`${this.props.dataSelector}-Package-errorContent`}
                errorCode={this.props.proposals.find(({ errorCode }) => !!errorCode)?.errorCode}
              />
            ) : null}
            {this.shouldDisplayModal() && (
              <AlternativeProposalsModal
                trackingDefaultPayload={{
                  categoryGA: 'resort',
                  actionGA: 'checkPricesKO',
                }}
                searchCriteria={{
                  arrivalDate: String(this.props.arrivalDate),
                  duration: this.props.duration,
                  adultsCount: this.props.adultsNumber,
                  kidsCount: this.props.kids.length,
                  departureCity: this.props.departureCity,
                }}
                i18n={i18n}
                onClose={this.props.clearProposals}
                onSubmit={this.redirectToBE}
                proposals={this.props.proposals}
                suggestions={this.props.suggestions}
                resort={this.props.resort}
                goToBookingEngine={this.goToBookingEngine}
                bookingEngineEntryPoint={this.props.bookingEngineEntry}
                scrollToBookingEngineEntryPoint={() => this.scrollToBookingEngineEntryPoint()}
                onSelectNewDuration={({ proposalIds }) => {
                  const queryParams = {
                    ...this.props.location.queryParams,
                    proposalIds,
                    selectedProposalId: proposalIds[0],
                  };
                  this.props.history.pushRoute(
                    routeBuilder.buildUrl('booking', { page: PAGE_BE_ACCOMMODATION }, queryParams),
                  );
                }}
              />
            )}
          </div>
        </form>
      </SiteContainer>
    );
  }
}
export default withRouter(BookingEngineForm);
